import { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { Models } from '../../../Utils/ModelsLMS';
import ConfirmDeleteDialog from '../../../../security/ConfirmDeleteDialog';
import { normalizeValue } from '../../../../../utils/Texts';
import axios from 'axios';
import Modal from './CursosModal';
import sigmaDelete from '../../../../Pages/img/Borrar.png';
import sigmaEdit from '../../../../Pages/img/Editar.png';
import AlertDialog from '../../Shared/AlertDialog';

const PAGE_SIZE = 10;
const nombreTabla = "Cursos";
const endpoint = ENDPOINTS.cursosAdmin;
const endpointById = ENDPOINTS.cursosById;
const modelo = Models.cursos;

const maps = {
  mapCreate: (f) => ({
    idInstructor: Number(f.idInstructor) || 0,
    nombre: f.nombre,
    idNivel: Number(f.idNivel) || 0,
    duracion: Number(f.duracion) || 0,
    idContenido: Number(f.idContenido) || 0,
    idTipoHabilidad: Number(f.idTipoHabilidad) || null,
    uRLRutaImagen: f.uRLRutaImagen,
    autoAsignable: f.autoAsignable ?? false,
    terminado: false,
    activo: true,
  }),
  mapUpdate: (id, f) => ({
    idCurso: id,
    idInstructor: Number(f.idInstructor) || 0,
    nombre: f.nombre,
    idNivel: Number(f.idNivel) || 0,
    duracion: Number(f.duracion) || 0,
    idContenido: Number(f.idContenido) || 0,
    idTipoHabilidad: Number(f.idTipoHabilidad) || null,
    uRLRutaImagen: f.uRLRutaImagen,
    autoAsignable: f.autoAsignable ?? false,
    terminado: f.terminado ?? false,
    activo: true,
  }),
};

export default function Catalog() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [instructores, setInstructores] = useState([]);
  const [niveles, setNiveles] = useState([]);
  const [tiposHabilidad, setTiposHabilidad] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [query, setQuery] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('new');
  const [selectedId, setSelectedId] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({ id: 0, name: '' });
  const [editData, setEditData] = useState(null);
  const [editingRowId, setEditingRowId] = useState(null);
  const [page, setPage] = useState(1);
  const [alertConfig, setAlertConfig] = useState({ open: false, message: '', title: 'Información', variant: 'info' });
  const [deleteIntegrity, setDeleteIntegrity] = useState({ carrerasPertenece: [], semestresAfectados: [], carrerasAfectadas: [] });
  const [deleteStep, setDeleteStep] = useState(0); // 0: cerrado, 1: Aviso de Carrera, 2: Aviso de Cascada

  const triggerAlert = (message, variant = 'error', title = 'Error') => {
    setModalOpen(false);
    setDeleteOpen(false);
    setDeleteStep(0);
    setAlertConfig({ open: true, message, variant, title });
  };

  const editorRef = useRef(null);

  useEffect(() => {
    if (modalOpen && editorRef.current) {
      editorRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [modalOpen, selectedId]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [resCursos, resInst, resNiv, resTipos, resEmp, resPer] = await Promise.all([
        axios.get(endpoint),
        axios.get(ENDPOINTS.instructor),
        axios.get(ENDPOINTS.catNivel),
        axios.get(ENDPOINTS.catTipoHabilidad),
        axios.get(ENDPOINTS.empleados),
        axios.get(ENDPOINTS.personas)
      ]);
      setRows((resCursos.data || []).map((x) => modelo(x)));
      setInstructores((resInst.data || []).map(x => Models.instructor(x)));
      setNiveles((resNiv.data || []).map(x => Models.niveles(x)));
      setTiposHabilidad((resTipos.data || []).map(x => Models.tipoHabilidad(x)));
      setEmpleados((resEmp.data || []).map(x => Models.empleado(x)));
      setPersonas((resPer.data || []).map(x => Models.personas(x)));
    } catch (e) {
      console.error(e);
      triggerAlert('Error al cargar información');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const getInstructorNombre = useCallback((idInstructor) => {
    const inst = instructores.find(i => i.idInstructor === idInstructor);
    if (!inst) return `Instructor ${idInstructor}`;
    const emp = empleados.find(e => e.idEmpleado === inst.idEmpleado);
    if (!emp) return `Instructor ${idInstructor}`;
    const per = personas.find(p => p.idPersona === emp.idPersona);
    return per ? `${per.nombre} ${per.apellidoPaterno} ${per.apellidoMaterno || ''}`.trim() : `Instructor ${idInstructor}`;
  }, [instructores, empleados, personas]);

  const getNivelNombre = useCallback((idNivel) => {
    const niv = niveles.find(n => n.idNivel === idNivel);
    return niv ? niv.nombre : `Nivel ${idNivel}`;
  }, [niveles]);

  const getTipoHabilidadNombre = useCallback((idTipoHabilidad) => {
    if (!idTipoHabilidad) return 'No especificado';
    const tipo = tiposHabilidad.find(t => t.idTipoHabilidad === idTipoHabilidad);
    return tipo ? tipo.nombre : `Tipo ${idTipoHabilidad}`;
  }, [tiposHabilidad]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const instNombre = getInstructorNombre(r.idInstructor).toLowerCase();
      const nom = (r.nombre || '').toLowerCase();
      const nivNombre = getNivelNombre(r.idNivel).toLowerCase();
      const tipoHabilidadNombre = getTipoHabilidadNombre(r.idTipoHabilidad).toLowerCase();
      const dur = String(r.duracion ?? '').toLowerCase();
      const cont = String(r.idContenido ?? '').toLowerCase();
      const url = (r.uRLRutaImagen || '').toLowerCase();
      const act = r.activo ? 'si' : 'no';
      return instNombre.includes(q) || nom.includes(q) || nivNombre.includes(q) || tipoHabilidadNombre.includes(q) || dur.includes(q) || cont.includes(q) || url.includes(q) || act.includes(q);
    });
  }, [rows, query, getInstructorNombre, getNivelNombre, getTipoHabilidadNombre]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  useEffect(() => { if (page > totalPages) setPage(1); }, [totalPages, page]);

  const paged = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  const handleNew = () => {
    setSelectedId(null);
    setEditData(null);
    setModalMode('new');
    setModalOpen(true);
    setEditingRowId(null);
  };

  const handleEdit = (id) => {
    const r = rows.find(x => x.idCurso === id);
    if (!r) {
      triggerAlert('No se encontró la fila en memoria.');
      return;
    }
    setSelectedId(id);
    setEditData(r);
    setModalMode('edit');
    setModalOpen(true);
    setEditingRowId(id);
  };

  const askDelete = async (id, name) => {
    setLoading(true);
    console.log('Iniciando verificación para curso ID:', id, 'Nombre:', name);
    try {
      const res = await axios.get(ENDPOINTS.cursosAsociacionesCarrera(id));
      const integrity = res.data;
      console.log('Integridad detectada:', integrity);
      
      setDeleteTarget({ id, name });
      setDeleteIntegrity(integrity);

      // Verificamos si existe alguna advertencia (asociación o cascada) - Soporta PascalCase y camelCase
      const carreras = integrity.carrerasPertenece || integrity.CarrerasPertenece;
      const semestres = integrity.semestresAfectados || integrity.SemestresAfectados;
      const carrerasAfe = integrity.carrerasAfectadas || integrity.CarrerasAfectadas;

      const tieneAsociaciones = Array.isArray(carreras) && carreras.length > 0;
      const tieneCascada = (Array.isArray(semestres) && semestres.length > 0) || 
                          (Array.isArray(carrerasAfe) && carrerasAfe.length > 0);

      if (tieneAsociaciones) {
        console.log('Bloqueando borrado: Curso asociado a carreras activas.');
        triggerAlert(
          "No puedes eliminar este curso porque se encuentra asociado a una o más carreras activas. Por seguridad de la trayectoria académica de los alumnos, debes desvincular el curso de las carreras correspondientes antes de intentar eliminarlo.",
          "error",
          "Acción Bloqueada"
        );
        return;
      }

      if (tieneCascada) {
        console.log('Se detectaron afectaciones por cascada, mostrando AlertDialog (Paso 1)');
        setDeleteStep(1);
      } else {
        console.log('No se detectó integridad afectada, pasando a ConfirmDeleteDialog');
        setDeleteStep(0);
        setDeleteOpen(true);
      }
    } catch (e) {
      console.error('Error en askDelete:', e);
      triggerAlert('Error al verificar integridad del curso');
    } finally {
      setLoading(false);
    }
  };

  const handleIntegrityNext = () => {
    setDeleteStep(0);
    setDeleteOpen(true); // Pasar al confirm delete dialog final
  };

  const doDelete = async () => {
    try {
      setLoading(true);
      await axios.delete(endpointById(deleteTarget.id));
      setDeleteOpen(false);
      setDeleteStep(0);
      setEditingRowId(null);
      await loadData();
    } catch (e) {
      console.error(e);
      triggerAlert('Error al eliminar');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (payload, mode) => {
    try {
      setLoading(true);
      if (mode === 'new') {
        const res = await axios.post(endpoint, maps.mapCreate(payload));
        const newId = res?.data?.idCurso ?? (typeof res?.data === 'number' ? res.data : null);
        if (newId) setEditingRowId(newId);
      } else {
        await axios.put(endpoint, maps.mapUpdate(selectedId, payload));
        setEditingRowId(selectedId);
      }
      setModalOpen(false);
      await loadData();
    } catch (e) {
      console.error(e);
      triggerAlert('Error al guardar');
    } finally {
      setLoading(false);
    }
  };

  const PageBtn = ({ label, onClick, active, disabled }) => (
    <button
      type="button"
      className={`roles-page-btn ${active ? 'is-active' : ''} ${disabled ? 'is-disabled' : ''}`}
      onClick={onClick}
      disabled={disabled}
    >{label}</button>
  );

  const pageNumbers = (() => {
    const maxToShow = 5;
    let start = Math.max(1, page - 2);
    let end = Math.min(totalPages, start + maxToShow - 1);
    start = Math.max(1, end - maxToShow + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  })();

  const startIndex = filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const endIndex = Math.min(page * PAGE_SIZE, filtered.length);

  return (
    <div className="panel-sigma-main">
      <div className="roles-shell">
        <div className="roles-topbar">
          <h2 className="roles-title">{nombreTabla}</h2>

          <input
            className="roles-search"
            placeholder="Buscar por nombre, instructor, nivel, duración, contenido o URL…"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setPage(1); }}
          />
          <button className="btn-action" onClick={handleNew}>＋ Nuevo</button>
        </div>

        <div className="roles-table-wrap">
          <div className="roles-table-scroller">
            <table className="roles-table">
              <thead>
                <tr>
                  <th>Instructor</th>
                  <th>Nombre</th>
                  <th>Nivel</th>
                  <th>Tipo de Habilidad</th>
                  <th style={{ textAlign: 'center' }}>Editar</th>
                  <th style={{ textAlign: 'center' }}>Borrar</th>
                </tr>
              </thead>
              <tbody>
                {paged.map((r) => (
                  <tr 
                    key={r.idCurso} 
                    className={`${editingRowId === r.idCurso ? 'is-editing' : ''} ${r.tieneCertificado ? 'is-certified' : ''}`}
                    style={r.tieneCertificado ? { backgroundColor: '#f1f5f9', color: '#64748b' } : {}}
                  >
                    <td>{getInstructorNombre(r.idInstructor)}</td>
                    <td><strong>{normalizeValue(r.nombre)}</strong></td>
                    <td>{getNivelNombre(r.idNivel)}</td>
                    <td>{getTipoHabilidadNombre(r.idTipoHabilidad)}</td>
                    <td style={{ textAlign: 'center' }}>
                      <button type="button" className="rk-iconbtn rk-iconbtn--edit" onClick={() => handleEdit(r.idCurso)} aria-label="Editar" title="Editar">
                        <img src={sigmaEdit} alt="" className="rk-ico" />
                      </button>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <button type="button" className="rk-iconbtn rk-iconbtn--del" onClick={() => { setEditingRowId(null); askDelete(r.idCurso, r.nombre || `Curso ${r.idCurso}`); }} aria-label="Borrar" title="Borrar">
                        <img src={sigmaDelete} alt="" className="rk-ico" />
                      </button>
                    </td>
                  </tr>
                ))}

                {!loading && paged.length === 0 && (<tr><td colSpan={9}>Sin resultados.</td></tr>)}
              </tbody >
            </table >
          </div >
        </div >

        <div className="roles-pagination">
          <div className="roles-pagination-left">
            {filtered.length > 0
              ? <span>Mostrando {startIndex}–{endIndex} de {filtered.length}</span>
              : <span>0 resultados</span>}
          </div>
          <div className="roles-pagination-right">
            <PageBtn label="‹" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} />
            {pageNumbers.map(n => (<PageBtn key={n} label={n} onClick={() => setPage(n)} active={n === page} />))}
            <PageBtn label="›" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} />
          </div>
        </div>

        {
          modalOpen && (
            <div className="roles-editor-slot" ref={editorRef}>
              <Modal
                open={modalOpen}
                mode={modalMode}
                data={editData}
                instructores={instructores}
                niveles={niveles}
                tiposHabilidad={tiposHabilidad}
                empleados={empleados}
                personas={personas}
                inline
                onClose={() => setModalOpen(false)}
                onSubmit={handleSubmit}
              />
            </div>
          )
        }
      </div >

      <ConfirmDeleteDialog
        open={deleteOpen}
        roleName={deleteTarget.name}
        onClose={() => setDeleteOpen(false)}
        onConfirm={doDelete}
      />

      {/* Alerta de Integridad Consolidada (Paso 1) */}
      <AlertDialog
        open={deleteStep === 1}
        title="Validación de Integridad"
        variant="warning"
        message={
          <div style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: '8px' }}>
            <p>Se han detectado las siguientes consecuencias al eliminar el curso <b>{deleteTarget.name}</b>:</p>
            
            {(deleteIntegrity.carrerasPertenece?.length > 0 || deleteIntegrity.CarrerasPertenece?.length > 0) && (
              <div style={{ marginTop: '1rem' }}>
                <p><b>Está asociado a las siguientes carreras activas:</b></p>
                <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                  {(deleteIntegrity.carrerasPertenece || deleteIntegrity.CarrerasPertenece).map((c, i) => <li key={i}>{c}</li>)}
                </ul>
              </div>
            )}

            {(deleteIntegrity.semestresAfectados?.length > 0 || deleteIntegrity.SemestresAfectados?.length > 0 || 
              deleteIntegrity.carrerasAfectadas?.length > 0 || deleteIntegrity.CarrerasAfectadas?.length > 0) && (
              <div style={{ marginTop: '1.5rem', padding: '12px', background: '#fff1f2', borderRadius: '8px', border: '1px solid #fda4af' }}>
                <p style={{ color: '#be123c', fontWeight: 'bold' }}>⚠️ Borrado en Cascada:</p>
                <p style={{ fontSize: '0.9rem', color: '#be123c' }}>Los siguientes elementos se desactivarán automáticamente por quedar vacíos:</p>
                
                {(deleteIntegrity.semestresAfectados?.length > 0 || deleteIntegrity.SemestresAfectados?.length > 0) && (
                  <div style={{ marginTop: '8px' }}>
                    <p style={{ fontSize: '0.85rem', fontWeight: 'bold' }}>Niveles (Semestres) afectados:</p>
                    <ul style={{ fontSize: '0.85rem', margin: '4px 0', paddingLeft: '20px' }}>
                      {(deleteIntegrity.semestresAfectados || deleteIntegrity.SemestresAfectados).map((s, i) => <li key={i}>{s}</li>)}
                    </ul>
                  </div>
                )}
                
                {(deleteIntegrity.carrerasAfectadas?.length > 0 || deleteIntegrity.CarrerasAfectadas?.length > 0) && (
                  <div style={{ marginTop: '8px' }}>
                    <p style={{ fontSize: '0.85rem', fontWeight: 'bold' }}>Carreras completas que se desactivarán:</p>
                    <ul style={{ fontSize: '0.85rem', margin: '4px 0', paddingLeft: '20px' }}>
                      {(deleteIntegrity.carrerasAfectadas || deleteIntegrity.CarrerasAfectadas).map((c, i) => <li key={i}>{c}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            )}
            
            <p style={{ marginTop: '1.5rem' }}>¿Estás seguro de que deseas continuar con la eliminación del curso?</p>
          </div>
        }
        buttonText="Sí, continuar"
        cancelText="Cancelar"
        onCancel={() => setDeleteStep(0)}
        onClose={handleIntegrityNext}
      />

      <AlertDialog
        open={alertConfig.open}
        title={alertConfig.title}
        message={alertConfig.message}
        variant={alertConfig.variant}
        onClose={() => setAlertConfig({ ...alertConfig, open: false })}
      />
    </div >
  );
}
